import { useQuery } from "react-query";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const parseApiError = async (response, fallback) => {
  let body = {};
  try {
    body = await response.json();
  } catch (e) {
    if (!response.ok) {
      throw new Error(fallback);
    }
    throw e;
  }
  if (!response.ok) {
    throw new Error(body?.error || fallback);
  }
  return body;
};

const useGetList = (
  schoolId,
  trainingId,
  accessToken,
  page = 1,
  pageSize = 30,
  filter
) => {
  return useQuery(
    [
      "slot-change-requests",
      schoolId,
      trainingId,
      accessToken,
      page,
      pageSize,
      filter,
    ],
    async () => {
      const status = filter?.status ?? "";
      const studentId = filter?.student_id ?? "";
      const studentName = filter?.student_name ?? "";
      const fromClassId = filter?.from_class_id ?? "";
      const urgency = filter?.urgency ?? "";
      const slotId = filter?.requested_slot_id ?? "";
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (status !== "") {
        params.set("status", status);
      }
      if (studentId !== "") {
        params.set("student_id", studentId);
      }
      if (studentName !== "") {
        params.set("student_name", studentName);
      }
      if (fromClassId !== "") {
        params.set("from_class_id", fromClassId);
      }
      if (urgency !== "") {
        params.set("urgency", urgency);
      }
      if (slotId !== "") {
        params.set("requested_slot_id", slotId);
      }
      const response = await fetch(
        BACKEND_URL +
          `/api/v1/schools/${schoolId}/trainings/${trainingId}/slot-change-requests?${params.toString()}`,
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + accessToken,
          },
        }
      );
      return parseApiError(
        response,
        "Erreur de récupération des demandes de changement de créneau!"
      );
    }
  );
};

const GetOne = (schoolId, trainingId, accessToken, requestId) => {
  return useQuery(
    ["slot-change-request", schoolId, trainingId, accessToken, requestId],
    async () => {
      const response = await fetch(
        BACKEND_URL +
          `/api/v1/schools/${schoolId}/trainings/${trainingId}/slot-change-requests/${requestId}`,
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + accessToken,
          },
        }
      );
      return parseApiError(
        response,
        "Erreur de récupération de la demande de changement de créneau!"
      );
    }
  );
};

const useGetProposals = (schoolId, trainingId, accessToken, enabled = true) => {
  return useQuery(
    ["slot-change-requests-proposals", schoolId, trainingId, accessToken],
    async () => {
      const response = await fetch(
        BACKEND_URL +
          `/api/v1/schools/${schoolId}/trainings/${trainingId}/slot-change-requests/proposals`,
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + accessToken,
          },
        }
      );
      return parseApiError(
        response,
        "Erreur de calcul des propositions d'affectation!"
      );
    },
    { enabled: enabled && !!schoolId && !!trainingId }
  );
};

const CreateOne = async (schoolId, trainingId, accessToken, data) => {
  const response = await fetch(
    BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/slot-change-requests",
    {
      method: "POST",
      body: JSON.stringify(data),
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  return parseApiError(
    response,
    "Erreur de création de la demande de changement de créneau!"
  );
};

const UpdateOne = async (schoolId, trainingId, accessToken, requestId, data) => {
  const response = await fetch(
    BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/slot-change-requests/" +
      requestId,
    {
      method: "PATCH",
      body: JSON.stringify(data),
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  return parseApiError(
    response,
    "Erreur de modification de la demande de changement de créneau!"
  );
};

const DeleteOne = async (schoolId, trainingId, accessToken, requestId) => {
  const response = await fetch(
    BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/slot-change-requests/" +
      requestId,
    {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  return parseApiError(
    response,
    "Erreur d'annulation de la demande de changement de créneau!"
  );
};

const RejectOne = async (
  schoolId,
  trainingId,
  accessToken,
  requestId,
  reason
) => {
  const response = await fetch(
    BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/slot-change-requests/" +
      requestId +
      "/reject",
    {
      method: "POST",
      body: JSON.stringify({ reason }),
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  return parseApiError(
    response,
    "Erreur de refus de la demande de changement de créneau!"
  );
};

const Apply = async (schoolId, trainingId, accessToken, items) => {
  const response = await fetch(
    BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/slot-change-requests/apply",
    {
      method: "POST",
      body: JSON.stringify({ items }),
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  return parseApiError(
    response,
    "Erreur d'application des changements de créneau!"
  );
};

const useGetParentsEmailPreview = (schoolId, trainingId, accessToken, requestId, enabled = true) => {
  return useQuery(
    ["slot-change-request-parents-email-preview", schoolId, trainingId, accessToken, requestId],
    async () => {
      const response = await fetch(
        BACKEND_URL +
          "/api/v1/schools/" +
          schoolId +
          "/trainings/" +
          trainingId +
          "/slot-change-requests/" +
          requestId +
          "/parents-email-preview",
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + accessToken,
          },
        }
      );
      return parseApiError(
        response,
        "Erreur de génération de l'aperçu du mail aux parents!"
      );
    },
    { enabled: enabled && !!schoolId && !!trainingId && !!requestId }
  );
};

const SendParentsEmail = async (schoolId, trainingId, accessToken, requestId) => {
  const response = await fetch(
    BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/slot-change-requests/" +
      requestId +
      "/send-parents-email",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  return parseApiError(
    response,
    "Erreur d'envoi du mail aux parents!"
  );
};

const ImportFromExcelFile = async (schoolId, trainingId, accessToken, file, name) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("name", name || file?.name || "import-demandes-changement-creneau");

  const response = await fetch(
    BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/tasks/import-slot-change-requests-from-excel-file",
    {
      method: "POST",
      body: formData,
      credentials: "include",
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  return parseApiError(
    response,
    "Erreur d'importation des demandes de changement de créneau!"
  );
};

const DownloadImportTemplate = async (schoolId, trainingId, accessToken) => {
  const response = await fetch(
    BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/slot-change-requests/import-template",
    {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  if (!response.ok) {
    let body = {};
    try {
      body = await response.json();
    } catch (e) {
      throw new Error("Erreur de téléchargement du modèle d'import!");
    }
    throw new Error(body?.error || "Erreur de téléchargement du modèle d'import!");
  }
  return response.blob();
};

const slotChangeRequestsAPI = {
  useGetList,
  GetOne,
  useGetProposals,
  CreateOne,
  UpdateOne,
  DeleteOne,
  RejectOne,
  Apply,
  useGetParentsEmailPreview,
  SendParentsEmail,
  ImportFromExcelFile,
  DownloadImportTemplate,
};

export default slotChangeRequestsAPI;
